import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  selectUser,
  setLinkRecords,
  selectLinkRecords,
  setCollections,
  setCollectionFavorites,
  setEditingCollection,
  setSnippets,
} from "../redux/features/appData";
import {
  User,
  LinkAppConfig,
  LinkRecord,
  Collection,
  Snippet,
} from "winslow-core";
import { useDispatch } from "react-redux";
import { apiGetWorkspaceLinkRecords, apiGetWorkspaceCollections } from "../api";

import { apiGetCollectionSnippets } from "../services/CollectionService";

export const useCoreDataLoader = (noFirstLoad = false) => {
  const dispatch = useDispatch();

  const storeUser = useSelector(selectUser);
  const linkRecords = useSelector(selectLinkRecords);

  const [apps, setApps] = useState<LinkAppConfig[]>([]);
  const [members, setMembers] = useState<User[]>([]);

  useEffect(() => {
    if (storeUser && !noFirstLoad) {
      (async () => {
        await loadAll(storeUser.currentWorkspaceId);
      })();
    }
  }, []);

  useEffect(() => {
    if (storeUser && !linkRecords && !noFirstLoad) {
      (async () => {
        await loadAll(storeUser.currentWorkspaceId);
      })();
    }
  }, [storeUser]);

  // Reload all data when workspace changes
  useEffect(() => {
    if (storeUser?.currentWorkspaceId && !noFirstLoad) {
      (async () => {
        await loadAll(storeUser.currentWorkspaceId);
      })();
    }
  }, [storeUser?.currentWorkspaceId]);

  const loadAll = async (
    workspaceId: string | undefined
  ): Promise<string | null> => {
    if (!storeUser || !workspaceId) {
      return null;
    }

    const useWorkspaceId = workspaceId
      ? workspaceId
      : storeUser.currentWorkspaceId;

    const collections = await apiGetWorkspaceCollections(useWorkspaceId);

    if (collections) {
      collections.sort((a: Collection, b: Collection) => {
        if (!a.createdAt || !b.createdAt) {
          return 1;
        }

        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
    }

    dispatch(setCollections(collections));

    let collectionId: string | null = null;

    // we use the most recently create one as it should be the only one right now
    if (collections && collections.length > 0) {
      dispatch(setEditingCollection(collections[0]));

      const snippets = await apiGetCollectionSnippets(collections[0].id);

      dispatch(setSnippets(snippets ? [...snippets] : ([] as Snippet[])));

      collectionId = collections[0].id;
    }

    // Always fetch linkRecords for the workspace, regardless of collections
    if (useWorkspaceId) {
      let theLinkRecords = await apiGetWorkspaceLinkRecords(useWorkspaceId);

      // sort by most recent
      if (theLinkRecords) {
        theLinkRecords.sort((a: LinkRecord, b: LinkRecord) => {
          if (!a.createdAt || !b.createdAt) {
            return 1;
          }
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
      }

      dispatch(setLinkRecords(theLinkRecords));
    }

    return collectionId;
  };

  const reloadAll = async (
    workspaceId: string | undefined
  ): Promise<string | null> => {
    return await loadAll(workspaceId);
  };

  return { apps, members, reloadAll };
};
